/*
 * Lotus global add-to-cart handler.
 *
 * Intercepts every <form> POST to /cart/add anywhere in the store and routes the
 * response through <cart-drawer>.renderContents() so the cart DRAWER opens
 * instead of the cart PAGE — without requiring each form to be wrapped in
 * <product-form> or carry Dawn's specific span/spinner markup.
 *
 * If the cart type is set to "page" in theme settings, this script is not
 * loaded (gated in theme.liquid), so behavior falls back to native form POST.
 */
(function () {
  'use strict';

  // Set true right before we navigate away to start checkout. The flag survives
  // the page's bfcache freeze, so a later 'back' into this page can detect that
  // we returned from a checkout navigation and recover the buttons.
  var leavingForCheckout = false;

  // Clear any button left in a loading/disabled state by an outbound navigation.
  // bfcache restores the DOM exactly as it was left, so .is-adding
  // (pointer-events:none) + aria-disabled freeze the button as unclickable.
  function resetStuckButtons() {
    var nodes = document.querySelectorAll(
      '.is-adding, .loading[aria-disabled="true"], [data-fp-checkout][aria-disabled="true"], [data-original-html]'
    );
    Array.prototype.forEach.call(nodes, function (el) {
      el.classList.remove('loading', 'is-adding');
      el.removeAttribute('aria-disabled');
      // Restore the original button label/markup if it was swapped for the
      // 'Adding…' spinner before navigating away (bfcache restores the swapped
      // markup otherwise, leaving the button stuck on 'Adding').
      var orig = el.getAttribute('data-original-html');
      if (orig !== null) {
        el.innerHTML = orig;
        el.removeAttribute('data-original-html');
      }
    });
  }

  // Market-aware root path. '/' on the primary market, or e.g. '/en-nz/' when
  // the shopper is in a subfolder market. Hardcoding '/' would drop the market
  // and force the primary currency (e.g. AUD) at checkout, so every cart /
  // checkout / discount URL we build must be prefixed with this.
  function marketRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  // Silently re-sync the cart drawer + cart count from the server. After a
  // bfcache Back restore the drawer HTML is whatever it was when the shopper
  // left — often empty, even though items were added before they navigated to
  // checkout. Re-fetch the cart sections and swap the markup in place, WITHOUT
  // opening the drawer (so this is invisible until the shopper opens it).
  function refreshCartDrawer() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') return;
    var toRender;
    try { toRender = cartDrawer.getSectionsToRender(); } catch (e) { return; }
    var ids = toRender.map(function (s) { return s.id; }).filter(Boolean);
    if (!ids.length) return;
    fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(',')), { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        toRender.forEach(function (section) {
          var html = data[section.id];
          if (!html) return;
          var el = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!el) return;
          try {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var src = parsed.querySelector(section.selector || '.shopify-section');
            if (src) el.innerHTML = src.innerHTML;
            // Keep the drawer's empty/has-items state in sync (the .is-empty
            // class lives on the <cart-drawer> wrapper, outside #CartDrawer).
            if (section.id === 'cart-drawer') {
              var freshWrap = parsed.querySelector('cart-drawer');
              if (freshWrap) cartDrawer.classList.toggle('is-empty', freshWrap.classList.contains('is-empty'));
            }
          } catch (e) { /* ignore malformed section */ }
        });
      })
      .catch(function () { /* best-effort */ });
  }

  function getCheckoutUrl() {
    // Apply the stashed discount via /discount/CODE on the way to checkout.
    // Prefixed with the market root so the shopper stays in their localized
    // market (correct currency) through to checkout.
    var root = marketRoot();
    var code = (typeof window.LOTUS_DISCOUNT_CODE === 'string' && window.LOTUS_DISCOUNT_CODE) || '';
    if (!code) return root + 'checkout';
    return root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(root + 'checkout');
  }

  function getCartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add';
  }

  function isCartAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    // Skip forms already handled by Dawn's <product-form> — its onSubmitHandler
    // calls preventDefault() and dispatches its own AJAX path.
    if (form.closest('product-form')) return false;
    var action = form.getAttribute('action') || '';
    if (!action) return false;
    // Match /cart/add and any localized prefix (e.g. /en-au/cart/add).
    return /\/cart\/add(\.js|\?|$)/.test(action) || action.indexOf(getCartAddUrl()) !== -1;
  }

  function showError(message) {
    // Soft-fail: log + alert. Dawn's per-form error wrappers aren't guaranteed
    // to exist on every card, so a minimal fallback is fine.
    console.error('[lotus add-to-cart]', message);
    if (message) {
      try {
        window.alert(message);
      } catch (e) { /* no-op */ }
    }
  }

  // Close the drawer when the overlay (click-outside) is clicked. Delegated on
  // document so it survives cart re-renders: Dawn binds the overlay's close
  // listener only in connectedCallback, which does NOT re-run when #CartDrawer's
  // innerHTML is swapped on add/qty change — so after the cart had items the
  // fresh overlay had no handler and click-outside stopped closing it.
  document.addEventListener('click', function (event) {
    var overlay = event.target.closest && event.target.closest('#CartDrawer-Overlay, .cart-drawer-lotus__overlay');
    if (!overlay) return;
    var drawer = overlay.closest('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  }, false);

  // Intercept the cart drawer's own 'Checkout' button so it routes through
  // /discount/CODE when a discount is stashed on the page. This catches the
  // standard drawer-checkout flow (after add-to-cart-drawer-open).
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('.cart-drawer-lotus__checkout');
    if (!btn) return;
    var code = (typeof window.LOTUS_DISCOUNT_CODE === 'string' && window.LOTUS_DISCOUNT_CODE) || '';
    if (!code) return; // No discount code stashed; let Shopify handle checkout natively
    event.preventDefault();
    leavingForCheckout = true;
    window.location.href = getCheckoutUrl();
  }, true);

  // Deal CTAs that are links, not forms — the hero 'Buy' button
  // ([data-hero-checkout]) and the sticky 'Get Black Friday Deal' bar
  // ([data-deal-checkout]). They should behave EXACTLY like the page's
  // Add-to-cart→checkout button (same product/variant, same BLACKFRIDAY discount,
  // same market-safe code path). So just trigger that button: prefer the
  // featured-product one (home), else any [data-fp-checkout] on the page (e.g.
  // the main product button on a product page). Falls back to the link's own
  // product (data-variant-id, hero only), then to its href when JS is off.
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-hero-checkout], [data-deal-checkout]');
    if (!link) return;
    if (link.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }

    var fpBtn = document.querySelector('.shopify-section--featured-product-lotus [data-fp-checkout]')
      || document.querySelector('[data-fp-checkout]');
    if (fpBtn) {
      // Mirror the page's 'Add to cart → checkout' button.
      event.preventDefault();
      link.classList.add('loading');
      fpBtn.click();
      return;
    }

    // Fallback: no featured-product section on this page — use the hero's own
    // product. Add in-session (never a permalink, which forces primary currency)
    // then route to the market-aware checkout.
    var variantId = link.getAttribute('data-variant-id');
    if (!variantId) return; // Let the href (product page) run.
    event.preventDefault();
    link.setAttribute('aria-disabled', 'true');
    link.classList.add('loading');
    leavingForCheckout = true;
    var fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', link.getAttribute('data-qty') || '1');
    fetch(getCartAddUrl(), {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: fd
    })
      .then(function () { window.location.href = getCheckoutUrl(); })
      .catch(function () { window.location.href = getCheckoutUrl(); });
  }, true);

  // The native dynamic-checkout button (Shop Pay / PayPal / Apple Pay / Google
  // Pay) navigates via Shopify's own code, so flag the outbound trip here too —
  // it lets us recover that button after a 'back' (see pageshow below).
  document.addEventListener('click', function (event) {
    if (event.target.closest && event.target.closest('.shopify-payment-button, .fp-express')) {
      leavingForCheckout = true;
    }
  }, true);

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!isCartAddForm(form)) return;

      // The triggering button can opt this submission out of the drawer flow
      // and route straight to checkout instead. Read from event.submitter (the
      // actual button clicked) so multiple buy buttons in one form work.
      var submitterBtn = event.submitter || form.querySelector('[type="submit"]');
      var goToCheckout = !!(submitterBtn && submitterBtn.getAttribute('data-fp-checkout') === 'true');

      // ===== Go straight to checkout =====
      // Deliberately NOT a cart permalink (/cart/{id}:{qty}). Cart permalinks
      // resolve their checkout in the store's PRIMARY market, so a NZ/EU shopper
      // gets charged the primary currency (AUD) — the exact bug reported. The
      // store's normal add-to-cart -> native checkout flow preserves the
      // shopper's market + currency (verified against the base theme showing
      // NZD). So we let the AJAX /cart/add below add the item IN-SESSION, then
      // route to checkout via getCheckoutUrl(), which applies the discount with
      // a market-aware /discount/CODE redirect and lets Shopify own the checkout.

      var cartDrawer = document.querySelector('cart-drawer');
      // No drawer on the page (cart_type === 'page') — let the native POST run.
      // The form's return_to input still routes it to checkout (+ discount).
      if (!cartDrawer) return;

      event.preventDefault();

      var submitButton = submitterBtn || form.querySelector('[type="submit"]');
      var originalButtonHTML = null;
      var addingLabel = (submitButton && submitButton.getAttribute('data-adding-text')) || 'Adding';
      if (submitButton) {
        if (submitButton.getAttribute('aria-disabled') === 'true') return;
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading', 'is-adding');
        originalButtonHTML = submitButton.innerHTML;
        // Stash the original markup on the element so it can be restored after a
        // Back/forward-cache restore (when we navigate to checkout the local
        // var is gone, but this attribute rides along in bfcache).
        submitButton.setAttribute('data-original-html', originalButtonHTML);
        submitButton.innerHTML =
          '<span class="lotus-add-spinner" aria-hidden="true"></span>' +
          '<span class="lotus-add-label">' + addingLabel + '</span>';
      }

      var formData = new FormData(form);
      if (cartDrawer && !goToCheckout) {
        try {
          var sections = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
        } catch (e) { /* getSectionsToRender unavailable; continue without */ }
        cartDrawer.setActiveElement && cartDrawer.setActiveElement(document.activeElement);
      }

      var config = {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      fetch(getCartAddUrl(), config)
        .then(function (r) { return r.json(); })
        .then(function (response) {
          if (response.status) {
            showError(response.description || response.message || 'Could not add to cart');
            return;
          }
          // Notify any subscribers (cart-icon-bubble, etc.)
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response
            });
          }
          if (goToCheckout) {
            // Skip the drawer entirely and route through /discount/CODE if a
            // section has stashed one — Shopify applies the discount on the
            // way to checkout.
            leavingForCheckout = true;
            window.location.href = getCheckoutUrl();
            return;
          }
          if (cartDrawer) cartDrawer.renderContents(response);
        })
        .catch(function (err) {
          showError(err && err.message);
        })
        .finally(function () {
          // Don't restore the button HTML if we're navigating away — avoids a
          // brief flicker back to the original price as the redirect kicks in.
          if (submitButton && !goToCheckout) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading', 'is-adding');
            if (originalButtonHTML !== null) submitButton.innerHTML = originalButtonHTML;
            submitButton.removeAttribute('data-original-html');
          }
        });
    },
    false
  );

  // ===== Back/forward cache recovery =====
  // When the shopper taps the browser's Back arrow after starting checkout, the
  // page is restored from bfcache exactly as they left it: our Add-to-Cart
  // button is frozen in its loading/disabled state (pointer-events:none), and
  // Shopify's dynamic-checkout web component does not re-initialise, so it's
  // inert too. Recover both.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return; // Only bfcache restores need recovery.
    // Re-enable our buttons. Deliberately do NOT reload the page: a reload
    // re-resolves the market and can repaint prices in the wrong currency when
    // the shopper taps Back. Letting bfcache restore the page as-is keeps the
    // correct localized currency on screen.
    resetStuckButtons();
    leavingForCheckout = false;
    // Best-effort: clear any stuck disabled state on the native payment button.
    var payBtns = document.querySelectorAll('.shopify-payment-button [disabled], .shopify-payment-button__button[disabled]');
    Array.prototype.forEach.call(payBtns, function (b) { b.removeAttribute('disabled'); });
    // The cart drawer markup restored from bfcache can be stale (e.g. empty even
    // though items were added before checkout). Re-sync it from the server.
    refreshCartDrawer();
  });
})();
